import { AxiosError, AxiosResponse } from 'axios'
import type { AnyAction, Dispatch } from 'redux'
import * as crmApi from '../../api/crm'
import { updateAppAlert } from '../appAlert/actions'
import { startLoading, stopLoading } from '../loading/actions'
import { LoadingTypes } from '../loading/reducer'
import { getErrorMessages } from '../../utils/error'
import * as errorLog from '../../utils/errorLogger'
import { RequestFailureMessage } from '../../utils/messagesContants'

export interface CrmAuthCreds {
  created_at?: string
  crm_auth_id?: string
  auth_key_name: string
  auth_key_value: string
}

export interface CrmAuthAppCreds {
  secret_key: string
  client_id: string
  app_name: string
  app_link: string
  version?: string
  secret_key_updated_at: string
}

export interface CrmAuthSchema {
  objects: { [key: string]: Record<string, unknown> }
  read_only_tables: string[]
  lead_operation_order: string[]
  send_notification_if: string[]
  lead_sync_result_table_name: string
  opportunity_operation_order: string[]
  opportunity_sync_result_table_name: string
  new_platform_sync: boolean
  canvas_migration_status?: 'pending' | 'in_progress' | 'completed' | null
  [key: string]: unknown
}

export interface CrmAuthFormData {
  created_at: string
  updated_at: string
  crm_id: string
  crm_type: string
  status: string
  environment_type: string
  ace_syncable_lead_field_key?: string
  ace_syncable_lead_field_value?: string
  ace_syncable_opp_field_key?: string
  ace_syncable_opp_field_value?: string
  ace_syncable_lead_parent_table?: string
  ace_syncable_opp_parent_table?: string
  ace_syncable_lead_field_data_type?: string
  ace_syncable_opp_field_data_type?: string
  last_connection_check_status?: string
  last_connection_checked_at?: string
  last_connection_result_message?: string
  deleted_at?: string
  creds?: CrmAuthCreds[]
  crm_app_creds?: CrmAuthAppCreds
  crm_schema?: CrmAuthSchema
}

export interface GetCRMFormDataOptions {
  /**
   * When `errorPresentation` is `'none'` (e.g. background onboarding-status
   * checks), a failure is still logged but does not surface a user-facing toast —
   * the caller treats missing CRM data as "not connected / incomplete".
   */
  errorPresentation?: 'toast' | 'none'
  /**
   * When `false`, fetches from `/crms/:crmId` without `?includes=creds,crm_app_creds`,
   * avoiding the transfer of sensitive credentials.  Use this for contexts that only
   * need `crm_schema` (e.g. HubSpot app cards checking `new_platform_sync`).
   * Defaults to `true` for backward compatibility.
   */
  includeCreds?: boolean
}

export const showCRMFormData = (data: CrmAuthFormData) => ({
  type: 'GET_CRM_FORM_DATA',
  payload: data,
})

/**
 * When `errorPresentation` is `'none'` (e.g. background onboarding-status
 * checks), a failure is still logged but does not surface a user-facing toast —
 * the caller treats missing CRM data as "not connected / incomplete".
 */
export const getCRMFormData =
  (
    crmId: string,
    {
      errorPresentation = 'toast',
      includeCreds = true,
    }: GetCRMFormDataOptions = {}
  ) =>
  async (
    dispatch: Dispatch<AnyAction>,
    getState: () => {
      CRMAuth: {
        crmFormData?: CrmAuthFormData | null
      }
    }
  ) => {
    try {
      dispatch(startLoading(LoadingTypes.CRM_AUTH))
      const existingFormData = getState().CRMAuth.crmFormData
      if (
        existingFormData?.crm_id === crmId &&
        existingFormData?.crm_schema?.new_platform_sync === true
      ) {
        return
      }
      const fetcher = includeCreds ? crmApi.getCRMAuth : crmApi.getCRMType
      const { data } = await fetcher(crmId)
      dispatch(showCRMFormData(data as CrmAuthFormData))
    } catch (error: unknown) {
      const e = error as AxiosError
      const errorResponse = e?.response as AxiosResponse<ErrorResponse>

      if (errorPresentation === 'toast') {
        dispatch(
          updateAppAlert({
            message: getErrorMessages([RequestFailureMessage])(errorResponse),
            messageType: 'ERROR',
            autoClose: true,
          })
        )
      }
      const globalState = getState()
      errorLog.errorLogger({ globalState })(
        error as Error,
        errorResponse?.data?.track_id
      )
    } finally {
      dispatch(stopLoading(LoadingTypes.CRM_AUTH))
    }
  }
